import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Grid, Chip,
  CircularProgress, Alert, Snackbar, IconButton, Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScienceIcon from '@mui/icons-material/Science';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, serverTimestamp, query, orderBy, where, runTransaction, doc, getDoc } from 'firebase/firestore';
import PaymentDialog from '../components/PaymentDialog';
import NewPatientDialog from '../components/NewPatientDialog';
import PatientSearch from '../components/PatientSearch';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { crearPresupuestoAutomatico } from '../services/presupuestoAutoService';
import { registrarVentaOrden } from '../services/ventaService';
import { printOrderPDF, calcAge } from './LabOrderDetail';

import { COLORS } from '../theme/constants';

const { primary: LAB_PRIMARY, teal: LAB_TEAL } = COLORS;

const getNextOrderNumber = async (retries = 3) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const counterRef = doc(db, 'counters', 'labOrders');
  
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const newCount = await runTransaction(db, async (tx) => {
        const snap = await tx.get(counterRef);
        const current = snap.exists() ? snap.data().lastNumber : 999;
        const next = current + 1;
        tx.set(counterRef, { lastNumber: next }, { merge: true });
        return next;
      });
      return `LAB-${y}${m}${d}-${newCount}`;
    } catch (err) {
      if (err.code === 'resource-exhausted' || err.code === 'aborted') {
        // Exponential backoff: 500ms, 1000ms, 2000ms
        const delay = Math.pow(2, attempt) * 500;
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      throw err;
    }
  }
  throw new Error('No se pudo generar el número de orden después de varios intentos');
};

export default function NewLabOrder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPatientId = searchParams.get('patientId');
  const homeUrl = urlPatientId ? `/patients/${urlPatientId}/record` : '/lab/orders';
  const { currentUser, userData } = useAuth();
  const { activeBranchId, activeBranch, filterByBranch } = useBranch();
  const isGestion = (userData?.role || '').toLowerCase() === 'gestion';

  const [professionals, setProfessionals] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [aranceles, setAranceles] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingPatient, setLoadingPatient] = useState(false);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedVet, setSelectedVet] = useState(null);
  const [selectedExams, setSelectedExams] = useState([]);
  const [priority, setPriority] = useState('routine');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [paymentOpen, setPaymentOpen]       = useState(false);
  const [createdOrder, setCreatedOrder]     = useState(null);
  const [newPatientOpen, setNewPatientOpen] = useState(false);

  useEffect(() => {
    const loadAll = async () => {
      const [proSnap, etSnap, aSnap] = await Promise.all([
        getDocs(query(collection(db, 'professionals'), where('active', '!=', false))),
        getDocs(query(collection(db, 'examTypes'), where('active', '!=', false))),
        getDocs(query(collection(db, 'aranceles'))),
      ]);

      setProfessionals(filterByBranch(proSnap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
      setExamTypes(filterByBranch(etSnap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })));
      setAranceles(filterByBranch(aSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.activo !== false), { includeUnassigned: true }));
      setLoadingData(false);
    };
    loadAll().catch(() => setLoadingData(false));
  }, []);

  // Auto-seleccionar veterinario logueado
  useEffect(() => {
    if (['médico veterinario', 'director médico'].includes(userData?.role?.toLowerCase()) && !selectedVet && professionals.length > 0) {
      const me = professionals.find((p) => p.id === userData.professionalId);
      if (me) setSelectedVet(me);
    }
  }, [professionals, userData, selectedVet]);

  // Auto-seleccionar paciente si viene de la URL
  useEffect(() => {
    if (urlPatientId && !selectedPatient) {
      setLoadingPatient(true);
      getDoc(doc(db, 'patients', urlPatientId))
        .then(snap => {
          if (snap.exists()) {
            setSelectedPatient({ id: snap.id, ...snap.data() });
          }
        })
        .finally(() => setLoadingPatient(false));
    }
  }, [urlPatientId, selectedPatient]);

  const displayedCategories = useMemo(() => {
    const normalize = (s) =>
      (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const preNames = new Set([
      'perfil bioquimico',
      'panel de coagulacion (tp + tppa)',
      'hemograma completo',
    ]);
    const sorted = [...examTypes].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
    );
    const pre = sorted.filter((e) => preNames.has(normalize(e.name)));
    const rest = sorted.filter((e) => !preNames.has(normalize(e.name)));
    const byCat = rest.reduce((acc, e) => {
      const cat = e.category || 'General';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(e);
      return acc;
    }, {});
    const out = [];
    if (pre.length) out.push({ category: 'Prequirúrgicos', exams: pre });
    Object.keys(byCat)
      .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
      .forEach((cat) => out.push({ category: cat, exams: byCat[cat] }));
    return out;
  }, [examTypes]);

  const normalizeStr = (s) =>
    (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

  const crearPresupuestoDesdeOrden = async (orderNumber, orderId, orderType, exams) => {
    if (!selectedPatient || exams.length === 0) return null;
    return crearPresupuestoAutomatico({
      orderId,
      orderType,
      orderNumber,
      patientId: selectedPatient.patientId || selectedPatient.id,
      patientName: selectedPatient.name,
      patientSpecies: selectedPatient.species || '',
      ownerId: selectedPatient.ownerId || '',
      ownerName: selectedPatient.ownerName || '',
      ownerPhone: selectedPatient.ownerPhone || '',
      branchId: activeBranchId || userData?.branchId || '',
      vetId: selectedVet?.id || currentUser?.uid || '',
      vetName: selectedVet ? `Dr/a. ${selectedVet.nombre} ${selectedVet.apellidos || ''}`.trim() : (userData?.name || currentUser?.email || ''),
      items: exams.map((e) => {
        const nameNorm = normalizeStr(e.name);
        const arancel = aranceles.find((a) => normalizeStr(a.prestacion) === nameNorm) ||
                        aranceles.find((a) => normalizeStr(a.prestacion).includes(nameNorm));
        return {
          arancel,
          nombre: e.name,
          cantidad: 1,
          precioFallback: e.precioArancel || Math.round((e.precioNeto || 0) * 1.19),
          tipoPrestacionFallback: 'Laboratorio',
        };
      }),
      notas: `Generado automáticamente desde orden ${orderNumber || ''}`,
      updateOrderCollection: 'labOrders',
    });
  };

  const handleSubmit = async () => {
    if (!selectedPatient) return setSnackbar({ open: true, message: 'Selecciona un paciente.', severity: 'warning' });
    if (!selectedVet) return setSnackbar({ open: true, message: 'Selecciona un médico veterinario.', severity: 'warning' });
    if (selectedExams.length === 0) return setSnackbar({ open: true, message: 'Selecciona al menos un tipo de examen.', severity: 'warning' });

    setSubmitting(true);
    try {
      const orderNumber = await getNextOrderNumber();
      const vetName = `Dr/a. ${selectedVet.nombre} ${selectedVet.apellidos || ''}`.trim();

      const orderData = {
        orderNumber,
        patientId: selectedPatient.patientId || selectedPatient.id,
        patientName: selectedPatient.name,
        patientSpecies: selectedPatient.species || '',
        patientBreed: selectedPatient.breed || '',
        patientSex: selectedPatient.genero || '',
        patientBirthdate: selectedPatient.birthDate || '',
        ownerId: selectedPatient.ownerId || '',
        ownerName: selectedPatient.ownerName || '',
        vetId: selectedVet.id,
        vetName,
        examsRequested: selectedExams.map((e) => ({
          examTypeId: e.id,
          examTypeName: e.name,
          precioNeto: e.precioNeto || 0,
          precioArancel: e.precioArancel || Math.round((e.precioNeto || 0) * 1.19),
        })),
        priority,
        notes,
        branchId: activeBranchId || '',
        status: 'solicitado',
        paymentStatus: 'no_pagado',
        createdBy: currentUser?.uid || '',
        createdAt: serverTimestamp(),
      };
      const orderRef = await addDoc(collection(db, 'labOrders'), orderData);

      await addDoc(collection(db, 'labAuditLog'), {
        orderId: orderRef.id,
        action: 'order_created',
        performedBy: currentUser?.uid || '',
        performedAt: serverTimestamp(),
        details: `Orden ${orderNumber} creada. Exámenes: ${selectedExams.map((e) => e.name).join(', ')}`,
      });

      setSnackbar({ open: true, message: `Orden ${orderNumber} creada exitosamente.`, severity: 'success' });
      let presupuesto = null;
      try { presupuesto = await crearPresupuestoDesdeOrden(orderNumber, orderRef.id, 'labOrder', selectedExams); } catch (err) { console.error('Error creando presupuesto:', err); }
      await printOrderPDF({ ...orderData, id: orderRef.id }, calcAge, activeBranch);
      if (isGestion) {
        setCreatedOrder({ id: orderRef.id, orderNumber, presupuestoId: presupuesto?.id || null, examsRequested: selectedExams.map((e) => ({ examTypeId: e.id, examTypeName: e.name, precioNeto: e.precioNeto || 0, precioArancel: e.precioArancel || Math.round((e.precioNeto || 0) * 1.19) })) });
        setPaymentOpen(true);
      } else {
        setTimeout(() => navigate(homeUrl), 1200);
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Error al crear la orden: ${err.message}`, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePay = async ({ paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes }) => {
    const venta = await registrarVentaOrden({
      presupuestoId: createdOrder.presupuestoId || null,
      orderId: createdOrder.id,
      orderCollection: 'labOrders',
      orderType: 'labOrder',
      orderNumber: createdOrder.orderNumber,
      patientId: selectedPatient?.patientId || selectedPatient?.id || '',
      patientName: selectedPatient?.name || '',
      patientSpecies: selectedPatient?.species || '',
      ownerId: selectedPatient?.ownerId || '',
      ownerName: selectedPatient?.ownerName || '',
      fallbackItems: (createdOrder.examsRequested || []).map((e) => ({
        nombre: e.examTypeName, cantidad: 1,
        precioUnitario: e.precioArancel || Math.round((e.precioNeto || 0) * 1.19),
        tipoPrestacion: 'Laboratorio', unidadNegocio: 'Laboratorio',
      })),
      pago: { paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes },
      branchId: activeBranchId || userData?.branchId || '',
      user: { uid: currentUser?.uid, name: userData?.name, email: currentUser?.email },
      orderExtraUpdate: { status: 'pending_upload' },
    });
    await addDoc(collection(db, 'labAuditLog'), {
      orderId: createdOrder.id,
      action: 'payment_registered',
      performedBy: currentUser?.uid || '',
      performedAt: serverTimestamp(),
      details: `Pago registrado. Total: $${venta.total}. Método: ${paymentMethod}. Boleta: ${boletaNumber || 'N/A'}`,
    });
    setPaymentOpen(false);
    navigate(homeUrl);
  };

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => urlPatientId ? navigate(`/patients/${urlPatientId}/record`) : navigate(homeUrl)}
          sx={{ color: LAB_PRIMARY, fontWeight: 600 }}>Volver</Button>
        <ScienceIcon sx={{ color: LAB_PRIMARY }} />
        <Typography variant="h5" fontWeight={700} color={LAB_PRIMARY}>Nueva Orden de Examen</Typography>
      </Box>
      <Grid container spacing={3}>
        {/* Left: patient + vet */}
        <Grid
          size={{
            xs: 12,
            md: 5
          }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY}>
                Paciente y Solicitante
              </Typography>
              {selectedPatient && (
                <IconButton size="small" onClick={() => setSelectedPatient(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {!selectedPatient ? (
              <>
                {loadingPatient ? (
                  <CircularProgress size={24} />
                ) : (
                  <PatientSearch
                    onPatientSelect={setSelectedPatient}
                    selectedPatient={selectedPatient}
                    onNewPatient={() => setNewPatientOpen(true)}
                    showFilters={true}
                    label="Buscar paciente"
                  />
                )}
              </>
            ) : (
              <Box sx={{ p: 2, mb: 2, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0' }}>
                <Typography variant="body2" fontWeight={700}>{selectedPatient.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedPatient.species} · {selectedPatient.breed} · {selectedPatient.genero}
                  {selectedPatient.weight ? ` · ${selectedPatient.weight} kg` : ''}
                </Typography>
                {selectedPatient.ownerName && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Tutor: {selectedPatient.ownerName}
                    {selectedPatient.ownerRut && ` (${selectedPatient.ownerRut})`}
                  </Typography>
                )}
              </Box>
            )}

            <Autocomplete
              options={professionals}
              getOptionLabel={(p) => `Dr/a. ${p.nombre} ${p.apellidos || ''}`}
              value={selectedVet}
              disabled
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label="Médico veterinario solicitante *" size="small" sx={{ mb: 2 }} />
              )}
              noOptionsText="Sin resultados"
            />

            <TextField
              select fullWidth size="small" label="Prioridad *"
              value={priority} onChange={(e) => setPriority(e.target.value)} sx={{ mb: 2 }}>
              <MenuItem value="routine">Rutina</MenuItem>
              <MenuItem value="urgent">Urgente 🔴</MenuItem>
            </TextField>

            <TextField
              fullWidth multiline rows={3} size="small"
              label="Notas clínicas relevantes"
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Paper>
        </Grid>

        {/* Right: exam selection */}
        <Grid
          size={{
            xs: 12,
            md: 7
          }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="subtitle1" fontWeight={700} color={LAB_PRIMARY} mb={2}>
              Tipos de Examen
            </Typography>

            {displayedCategories.map(({ category, exams }) => (
              <Box key={category} sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color={LAB_TEAL}
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                  {category}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  {exams.map((e) => {
                    const selected = selectedExams.some((s) => s.id === e.id);
                    return (
                      <Chip
                        key={e.id}
                        label={e.name}
                        onClick={() => setSelectedExams((prev) =>
                          selected ? prev.filter((s) => s.id !== e.id) : [...prev, e]
                        )}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{
                          bgcolor: selected ? LAB_PRIMARY : 'transparent',
                          color: selected ? '#fff' : LAB_PRIMARY,
                          borderColor: LAB_PRIMARY,
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: selected ? '#0F2A45' : '#EFF6FF' },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            ))}

            {examTypes.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No hay tipos de examen configurados.{' '}
                <Button size="small" onClick={() => navigate('/lab/catalog')}>Configurar catálogo</Button>
              </Alert>
            )}

            {selectedExams.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#EFF6FF', borderRadius: 2, border: '1px solid #BFDBFE' }}>
                <Typography variant="caption" fontWeight={700} color={LAB_PRIMARY}>
                  Exámenes seleccionados ({selectedExams.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {selectedExams.map((e) => (
                    <Chip key={e.id} label={e.name} size="small"
                      onDelete={() => setSelectedExams((prev) => prev.filter((s) => s.id !== e.id))}
                      sx={{ bgcolor: LAB_PRIMARY, color: '#fff', fontSize: '0.72rem' }} />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate(homeUrl)} sx={{ mr: 2 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ScienceIcon />}
              sx={{ bgcolor: LAB_PRIMARY, fontWeight: 700, px: 4, '&:hover': { bgcolor: '#0F2A45' } }}>
              {submitting ? 'Creando...' : isGestion ? 'Crear Orden y Pagar' : 'Crear Orden'}
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <NewPatientDialog
        open={newPatientOpen}
        onClose={() => setNewPatientOpen(false)}
        onSaved={(newP) => {
          setSelectedPatient(newP);
        }}
      />
      <PaymentDialog
        open={paymentOpen}
        onClose={() => { setPaymentOpen(false); navigate(homeUrl); }}
        onPay={handlePay}
        exams={createdOrder?.examsRequested || []}
        orderNumber={createdOrder?.orderNumber || ''}
      />
    </Box>
  );
}
